import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, Gift } from "lucide-react";
import { useProducts } from "@/hooks/useProducts";
import { useCart } from "@/context/CartContext";
import { euro, CATEGORIES } from "@/lib/api";
import { MaskLine } from "@/components/site/Reveal";
import { useSeo, SITE_URL } from "@/lib/seo";

const MIN = 2;

function Pick({ p, qty, onAdd, onRemove }) {
  return (
    <div data-testid={`composer-product-${p.slug}`} className={`group relative flex flex-col rounded-sm border p-3 transition-colors ${qty ? "border-[var(--gold)] bg-[rgba(217,180,106,0.06)]" : "border-[var(--line)] hover:border-[var(--gold)]/50"}`}>
      <div className="relative aspect-square overflow-hidden rounded-sm bg-[var(--ink-2)]">
        <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        {qty > 0 && <span className="absolute right-2 top-2 flex h-7 min-w-7 items-center justify-center rounded-full bg-[var(--gold)] px-2 text-xs font-semibold text-[var(--ink)]">{qty}</span>}
      </div>
      <p className="mt-3 line-clamp-2 min-h-[2.5rem] text-sm leading-tight">{p.name}</p>
      <div className="mt-2 flex items-center justify-between">
        <span className="font-display text-lg text-[var(--gold-soft)]">{euro(p.price)}</span>
        <div className="flex items-center gap-1">
          {qty > 0 && <button data-testid={`composer-remove-${p.slug}`} onClick={onRemove} className="rounded-full border border-[var(--line)] p-1.5 hover:border-[var(--gold)]"><Minus className="h-3.5 w-3.5" /></button>}
          <button data-testid={`composer-add-${p.slug}`} onClick={onAdd} className="rounded-full bg-[var(--gold)] p-1.5 text-[var(--ink)] hover:bg-[var(--gold-soft)]"><Plus className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    </div>
  );
}

function BoxSummary({ lines, total, count, onAdd, onRemove, onSubmit }) {
  return (
    <div data-testid="composer-summary" className="rounded-sm border border-[var(--gold)]/40 bg-[var(--ink-2)] p-6">
      <div className="flex items-center gap-3"><Gift className="h-5 w-5 text-[var(--gold)]" /><p className="font-display text-2xl italic">Mon pack</p></div>
      <p className="mt-1 text-xs text-[var(--muted)]">{count} soin{count > 1 ? "s" : ""} · minimum {MIN}</p>
      <div className="mt-5 space-y-3">
        <AnimatePresence initial={false}>
          {lines.map(({ p, qty }) => (
            <motion.div key={p.slug} layout initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex items-center gap-3">
              <img src={p.image} alt="" className="h-12 w-12 rounded-sm object-cover" />
              <span className="flex-1 text-sm leading-tight">{p.name}</span>
              <span className="flex items-center gap-1 text-sm">
                <button onClick={() => onRemove(p.slug)} className="p-1"><Minus className="h-3 w-3" /></button>{qty}<button onClick={() => onAdd(p.slug)} className="p-1"><Plus className="h-3 w-3" /></button>
              </span>
            </motion.div>
          ))}
        </AnimatePresence>
        {!lines.length && <p className="py-6 text-center text-sm italic text-[var(--muted)]">Ajoutez vos soins préférés pour composer votre rituel.</p>}
      </div>
      <div className="mt-6 flex items-baseline justify-between border-t border-[var(--line)] pt-4">
        <span className="text-sm text-[var(--muted)]">Total du pack</span>
        <span data-testid="composer-total" className="font-display text-3xl text-[var(--gold-soft)]">{euro(total)}</span>
      </div>
      <button data-testid="composer-submit" disabled={count < MIN} onClick={onSubmit} className="mt-5 h-14 w-full rounded-full bg-[var(--gold)] text-xs font-semibold uppercase tracking-[0.22em] text-[var(--ink)] transition-colors hover:bg-[var(--gold-soft)] disabled:opacity-40">
        {count < MIN ? `Encore ${MIN - count} soin${MIN - count > 1 ? "s" : ""}` : "Ajouter mon pack au panier"}
      </button>
    </div>
  );
}

export default function Composer() {
  useSeo({
    title: "Composez votre pack de soins sur-mesure | Ozentya",
    description: "Créez votre pack Ozentya sur-mesure en choisissant vos soins cheveux, locks, barbe, visage et corps. Livraison gratuite en Point Relais en France, tarif unique DOM-TOM.",
    path: "/composer",
    jsonLd: { "@context": "https://schema.org", "@type": "WebPage", name: "Composez votre pack de soins sur-mesure", url: `${SITE_URL}/composer`, isPartOf: { "@id": `${SITE_URL}/#website` }, inLanguage: "fr-FR" },
  });
  const { data } = useProducts({ type: "unite" });
  const products = Array.isArray(data) ? data : [];
  const { addBox } = useCart();
  const [sel, setSel] = useState({});
  const [cat, setCat] = useState("");

  const add = (slug) => setSel((s) => ({ ...s, [slug]: (s[slug] || 0) + 1 }));
  const remove = (slug) => setSel((s) => { const n = { ...s }; n[slug] > 1 ? (n[slug] -= 1) : delete n[slug]; return n; });
  const lines = useMemo(() => products.filter((p) => sel[p.slug]).map((p) => ({ p, qty: sel[p.slug] })), [products, sel]);
  const count = lines.reduce((s, l) => s + l.qty, 0);
  const total = lines.reduce((s, l) => s + l.qty * l.p.price, 0);
  const shown = products.filter((p) => !cat || p.category === cat);

  const submit = () => {
    addBox(lines.map(({ p, qty }) => ({ slug: p.slug, name: p.name, price: p.price, image: p.image, qty })));
    setSel({});
  };

  return (
    <main data-testid="composer-page" className="mx-auto max-w-[1440px] px-5 pb-32 pt-44 md:px-10">
      <p className="eyebrow mb-6">Sur-mesure</p>
      <h1 className="font-display text-5xl leading-none md:text-8xl">
        <MaskLine>Composez</MaskLine>{" "}
        <MaskLine delay={0.15}><span className="italic gold-text">votre pack.</span></MaskLine>
      </h1>
      <p className="mt-8 max-w-lg text-base text-[var(--muted)]">Choisissez librement vos soins parmi toute la gamme Ozentya et créez un rituel qui vous ressemble.</p>
      <div className="mt-12 grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div className="mb-6 flex flex-wrap gap-2">
            {["", ...CATEGORIES].map((c) => (
              <button key={c || "all"} data-testid={`composer-filter-${c || "all"}`} onClick={() => setCat(c)} className={`h-10 rounded-full border px-5 text-xs uppercase tracking-[0.18em] transition-colors ${cat === c ? "border-[var(--gold)] bg-[var(--gold)] text-[var(--ink)]" : "border-[var(--line)] hover:border-[var(--gold)]"}`}>{c || "Tous"}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {shown.map((p) => <Pick key={p.slug} p={p} qty={sel[p.slug] || 0} onAdd={() => add(p.slug)} onRemove={() => remove(p.slug)} />)}
          </div>
        </div>
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-36">
            <BoxSummary lines={lines} total={total} count={count} onAdd={add} onRemove={remove} onSubmit={submit} />
          </div>
        </div>
      </div>
    </main>
  );
}
